import axios from 'axios';

const INACTIVITY_MS = 15 * 60 * 1000;
const HEARTBEAT_MS = 60 * 1000;
const POLL_MS = 30 * 1000;
const ACTIVITY_THROTTLE_MS = 30 * 1000;

function presenceClientUuid() {
	if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
	return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = Math.random() * 16 | 0;
		return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
	});
}

export default {
	data() {
		return {
			presenceClientId: presenceClientUuid(),
			presenceActive: false,
			presenceBannerDismissed: false,
			presenceLastActivityAt: Date.now(),
			presenceOtherEditors: [],
			presenceModalInactive: false,
			presenceActivityThrottled: false,
			presenceHeartbeatTimer: null,
			presencePollTimer: null,
			presenceInactivityTimer: null,
			presenceUnloadHandler: null,
			presenceStopped: false,
		};
	},
	computed: {
		presenceWarningText() {
			const names = this.presenceOtherEditors.map((editor) => editor.user_name);
			if (names.length === 1) {
				return this._lang('{name} is also editing this page.').replace('{name}', names[0]);
			}
			return this._lang('{names} are also editing this page.').replace('{names}', names.join(', '));
		},
	},
	watch: {
		edited() {
			this.touchPresenceActivity();
		},
		presenceOtherEditors() {
			this.presenceBannerDismissed = false;
		},
		page_id(newVal, oldVal) {
			if (!oldVal || newVal === oldVal) return;
			this.stopPagePresence();
			this.presenceClientId = presenceClientUuid();
			this.startPagePresence();
		},
	},
	beforeRouteLeave(to, from, next) {
		this.stopPagePresence();
		next();
	},
	beforeDestroy() {
		this.stopPagePresence();
	},
	methods: {
		startPagePresence() {
			if (!this.page_id) return;

			this.presenceStopped = false;
			this.presenceActive = true;
			this.presenceModalInactive = false;
			this.presenceLastActivityAt = Date.now();
			this.touchPresenceActivity();
			this.sendPresenceHeartbeat();
			this.startPresenceHeartbeat();
			this.pollPresenceEditors();
			this.bindPresenceActivity();
			this.schedulePresenceInactivityCheck();
			this.bindPresenceUnload();
		},

		stopPagePresence() {
			if (this.presenceStopped) return;
			this.presenceStopped = true;

			this.presenceActive = false;
			this.unbindPresenceActivity();
			this.unbindPresenceUnload();
			clearInterval(this.presenceHeartbeatTimer);
			clearInterval(this.presencePollTimer);
			clearTimeout(this.presenceInactivityTimer);
			this.presenceHeartbeatTimer = null;
			this.presencePollTimer = null;
			this.presenceInactivityTimer = null;
			this.leavePagePresence();
		},

		touchPresenceActivity() {
			if (this.presenceModalInactive) return;

			this.presenceLastActivityAt = Date.now();

			if (this.presenceActivityThrottled) return;
			this.presenceActivityThrottled = true;
			setTimeout(() => {
				this.presenceActivityThrottled = false;
			}, ACTIVITY_THROTTLE_MS);

			if (!this.presenceActive) return;
			this.schedulePresenceInactivityCheck();
		},

		schedulePresenceInactivityCheck() {
			clearTimeout(this.presenceInactivityTimer);
			this.presenceInactivityTimer = setTimeout(() => {
				this.onPresenceInactivity();
			}, INACTIVITY_MS);
		},

		onPresenceInactivity() {
			if (Date.now() - this.presenceLastActivityAt < INACTIVITY_MS) {
				this.schedulePresenceInactivityCheck();
				return;
			}

			this.presenceActive = false;
			this.presenceModalInactive = true;
			clearInterval(this.presenceHeartbeatTimer);
			this.presenceHeartbeatTimer = null;
		},

		stayOnPagePresence() {
			this.presenceModalInactive = false;
			this.presenceActive = true;
			this.presenceLastActivityAt = Date.now();
			this.sendPresenceHeartbeat();
			this.startPresenceHeartbeat();
			this.schedulePresenceInactivityCheck();
		},

		exitPagePresence() {
			this.stopPagePresence();
			this.$router.push('/project/' + this.id);
		},

		sendPresenceHeartbeat() {
			if (!this.page_id || !this.presenceClientId) return;

			return axios.post('/api/page/' + this.page_id + '/presence', {
				client_id: this.presenceClientId,
			}).then((res) => {
				this.updatePresenceOtherEditors(res.data.editors);
			}).catch(() => {});
		},

		pollPresenceEditors() {
			if (!this.page_id) return;

			axios.get('/api/page/' + this.page_id + '/presence').then((res) => {
				this.updatePresenceOtherEditors(res.data.editors);
			}).catch(() => {});

			clearInterval(this.presencePollTimer);
			this.presencePollTimer = setInterval(() => {
				if (!this.presenceActive && this.presenceModalInactive) return;

				axios.get('/api/page/' + this.page_id + '/presence').then((res) => {
					this.updatePresenceOtherEditors(res.data.editors);
				}).catch(() => {});
			}, POLL_MS);
		},

		startPresenceHeartbeat() {
			clearInterval(this.presenceHeartbeatTimer);
			this.presenceHeartbeatTimer = setInterval(() => {
				if (!this.presenceActive) return;
				this.sendPresenceHeartbeat();
			}, HEARTBEAT_MS);
		},

		updatePresenceOtherEditors(editors) {
			const userId = this.$store.getters.user.id;
			this.presenceOtherEditors = (editors || []).filter((editor) => editor.user_id !== userId);
		},

		leavePagePresence() {
			if (!this.page_id || !this.presenceClientId) return;

			const url = '/api/page/' + this.page_id + '/presence/leave';
			const payload = JSON.stringify({ client_id: this.presenceClientId });

			if (navigator.sendBeacon) {
				navigator.sendBeacon(url, new Blob([payload], { type: 'application/json' }));
				return;
			}

			axios.post(url, { client_id: this.presenceClientId }).catch(() => {});
		},

		bindPresenceActivity() {
			this.presenceActivityHandler = () => this.touchPresenceActivity();
			['mousedown', 'keydown', 'click', 'scroll'].forEach((event) => {
				window.addEventListener(event, this.presenceActivityHandler, { passive: true });
			});
		},

		unbindPresenceActivity() {
			if (!this.presenceActivityHandler) return;
			['mousedown', 'keydown', 'click', 'scroll'].forEach((event) => {
				window.removeEventListener(event, this.presenceActivityHandler);
			});
			this.presenceActivityHandler = null;
		},

		bindPresenceUnload() {
			this.presenceUnloadHandler = () => this.leavePagePresence();
			window.addEventListener('pagehide', this.presenceUnloadHandler);
		},

		unbindPresenceUnload() {
			if (!this.presenceUnloadHandler) return;
			window.removeEventListener('pagehide', this.presenceUnloadHandler);
			this.presenceUnloadHandler = null;
		},
	},
};
